import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import CoolIcon from '../components/ui/CoolIcon';

// Toasts. One item = { id, type, title, description?, action?, duration, fading }.
//   type:        'success' | 'info' | 'warning' | 'error'
//   title:       one bold line (the "message" every existing caller passes)
//   description: optional body text, lives in the collapsible section
//   action:      optional { label, onClick }, an outline button in the body
//                ("Okay"); clicking it runs onClick and closes the toast
//   duration:    ms before auto-close (default AUTO_CLOSE_MS); 0 = stays open
//   countdown:   true → show the "This message will close in N seconds. Click to
//                stop." footer + progress bar (default false: the timer runs silently)
//   Firing the SAME toast again (type + title + description) while it's still
//   showing does NOT stack a second card, the existing one restarts its timer.
//
// notify('success', 'Saved vendor X'), string shape (all existing callers)
// notify({ type: 'error', title: 'Could not save', description: err.message })
// notifySuccess('Changes saved', { description: '…', action: { label: 'Okay', onClick } })
//
// Anatomy (white card, same for every type, only the icon / bar colour changes):
//   ┌ (✓)  Title                          ∨  × ┐   header, ∨/∧ collapses the body
//   │      description … [ Okay ]              │   body (only when there is one)
//   │ This message will close in 9 seconds.    │   footer, click = stop auto-close
//   │ Click to stop.                           │
//   └━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┘   progress bar shrinks over the timer

const AUTO_CLOSE_MS = 10000;   // visible (per-toast `duration` overrides; 0 = sticky)
const FADE_MS       = 380;     // CSS exit transition

const NotificationContext = createContext(null);

// A single-string error/warning like "Could not save order: Network error"
// reads better as title "Could not save order" + description "Network error".
// Only splits on ": " (colon + space) so times ("3:00 PM") never trip it.
function splitMessage(type, message) {
    const text = String(message ?? '').trim();
    if (type !== 'error' && type !== 'warning') return { title: text, description: '' };
    const idx = text.indexOf(': ');
    if (idx <= 0 || idx > 60) return { title: text, description: '' };
    const description = text.slice(idx + 2).trim();
    if (!description) return { title: text, description: '' };
    return { title: text.slice(0, idx).trim(), description };
}

export function NotificationProvider({ children }) {
    const [items, setItems] = useState([]);
    const counterRef = useRef(0);
    const fadeTimers = useRef(new Map());
    // Synchronous registry of on-screen toasts by content key, React state
    // lags a tick, so five identical notify() calls in one event would all
    // slip past a state-based check.
    const liveKeys   = useRef(new Map());   // "type|title|description" → id
    const idKeys     = useRef(new Map());   // id → key

    const forget = (id) => {
        const key = idKeys.current.get(id);
        if (key != null && liveKeys.current.get(key) === id) liveKeys.current.delete(key);
        idKeys.current.delete(id);
    };

    const dismiss = useCallback((id) => {
        const t = fadeTimers.current.get(id);
        if (t) { clearTimeout(t); fadeTimers.current.delete(id); }
        forget(id);
        setItems(prev => prev.filter(n => n.id !== id));
    }, []);

    // Exit: fade first, then drop, the toast itself calls this when its
    // countdown reaches zero (or × / an action button is clicked).
    const close = useCallback((id) => {
        if (fadeTimers.current.has(id)) return;
        forget(id);   // a repeat fired during the fade-out gets a fresh toast
        setItems(prev => prev.map(n => n.id === id ? { ...n, fading: true } : n));
        fadeTimers.current.set(id, setTimeout(() => dismiss(id), FADE_MS));
    }, [dismiss]);

    const notify = useCallback((typeOrObj, maybeMessage, opts = {}) => {
        const spec = typeof typeOrObj === 'string'
            ? { type: typeOrObj, message: maybeMessage, ...(opts || {}) }
            : (typeOrObj || {});
        const type = spec.type || 'success';
        const raw  = spec.title || spec.message || '';
        if (!raw) return null;

        const split = spec.description ? { title: raw, description: spec.description } : splitMessage(type, raw);
        const description = split.description || '';
        // Repeat of a toast that's still on screen (rapid double-clicks on
        // "copy", a retried save…) → restart that toast instead of stacking.
        const key = `${type}|${split.title}|${description}`;
        const liveId = liveKeys.current.get(key);
        if (liveId != null) {
            setItems(prev => prev.map(n => n.id === liveId ? { ...n, nonce: (n.nonce || 0) + 1 } : n));
            return liveId;
        }
        const id = ++counterRef.current;
        liveKeys.current.set(key, id);
        idKeys.current.set(id, key);
        setItems(prev => [...prev, {
            id,
            type,
            title:       split.title,
            description,
            action:      spec.action && spec.action.label ? spec.action : null,
            duration:    Number.isFinite(spec.duration) ? Math.max(0, spec.duration) : AUTO_CLOSE_MS,
            countdown:   !!spec.countdown,
            nonce:       0,
            fading:      false,
        }]);
        return id;
    }, []);

    useEffect(() => {
        const timers = fadeTimers.current;
        return () => { timers.forEach(clearTimeout); timers.clear(); };
    }, []);

    // Stable identity: consumers list notifySuccess/notifyError in their own
    // useCallback deps, so a fresh object per toast tick would re-render them all.
    const value = useMemo(() => ({
        notify,
        notifySuccess: (msg, opts) => notify('success', msg, opts),
        notifyInfo:    (msg, opts) => notify('info',    msg, opts),
        notifyWarning: (msg, opts) => notify('warning', msg, opts),
        notifyError:   (msg, opts) => notify('error',   msg, opts),
        dismiss: close,
    }), [notify, close]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <NotificationStack items={items} onClose={close} fadeMs={FADE_MS} />
        </NotificationContext.Provider>
    );
}

// No-op fallback if a component is rendered outside the provider (stable identity).
const NOOP_NOTIFY = {
    notify: () => null,
    notifySuccess: () => null,
    notifyInfo: () => null,
    notifyWarning: () => null,
    notifyError: () => null,
    dismiss: () => {},
};

export function useNotify() {
    return useContext(NotificationContext) || NOOP_NOTIFY;
}

// ─── UI ───────────────────────────────────────────────────────────────────────

const TONES = {
    success: { icon: 'check',   color: '#16A34A', label: 'Success' },
    info:    { icon: 'info',    color: '#2563EB', label: 'Info' },
    warning: { icon: 'warning', color: '#CA8A04', label: 'Warning' },
    error:   { icon: 'error',   color: '#DC2626', label: 'Error' },
};

function ToastIcon({ kind, color }) {
    const name = { check: 'Circle_Check', warning: 'Triangle_Warning', error: 'Close_Circle' }[kind] || 'Info';
    return <CoolIcon name={name} size={18} strokeWidth={1.9} stroke={color} />;
}

const Chevron = ({ up }) => (
    <CoolIcon name="Chevron_Down" size={12} strokeWidth={2.74} style={{ transform: up ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
);
const Cross = () => (
    <CoolIcon name="Close_MD" size={11} strokeWidth={2.95} />
);

const ICON_BTN = 'flex h-[22px] w-[22px] items-center justify-center rounded-[6px] text-[#8A8A87] transition-colors hover:bg-[#F4F4F3] hover:text-[#1A1A1A]';

// The card is styled like the buttons (owner, 2026-10-10: "slimmer, less
// tall, less gap, less rounded, make it the style of buttons"): 8px corners,
// the buttons' thin #E4E4E1 edge, a 14px/500 title ("a bit bigger") and an
// 18px tone icon ("2px bigger"). The stack keeps 6px between cards.
//
// Balanced padding (owner, same day): everything sits in ONE padded row, so
// the ink is the same ~12.5px from all four edges. The paddings are uneven
// on purpose, because each edge's first ink sits at a different depth inside
// its box: the tone icon's circle starts 1.5px inside its 18px box (and the
// box is centered on the 20px title line), the close cross 7.6px inside its
// 22px button. So 10px left, 9px top and bottom, 4px right. The icon buttons
// hang 1px over the title line (-my-[1px]) instead of making the row taller,
// and an action button keeps 3px under it, a box edge reading closer than ink.

// One toast. Owns its countdown so "Click to stop" can cancel it; the provider
// only learns about the exit (onClose) when the timer hits zero or × / the
// action button is clicked. Exported so /settings/toasts can lay every state
// out side by side with the real card.
export function Toast({ item, onClose, fadeMs }) {
    const tone = TONES[item.type] || TONES.success;
    const hasBody = !!(item.description || item.action);
    const [collapsed, setCollapsed] = useState(false);
    const [stopped, setStopped]     = useState(item.duration <= 0);
    const totalSec = Math.max(1, Math.ceil(item.duration / 1000));
    const [secondsLeft, setSecondsLeft] = useState(totalSec);
    const startedAt = useRef(Date.now());

    // A repeat of this toast (same type/title/description) bumps `nonce`:
    // restart the clock (and un-stop it) so it reads as one refreshed message.
    useEffect(() => {
        if (!item.nonce) return;
        startedAt.current = Date.now();
        setSecondsLeft(totalSec);
        setStopped(item.duration <= 0);
    }, [item.nonce, item.duration, totalSec]);

    // Countdown, one tick a second for the label; the bar animates itself.
    useEffect(() => {
        if (stopped || item.fading) return undefined;
        const tick = () => {
            const left = Math.max(0, Math.ceil((item.duration - (Date.now() - startedAt.current)) / 1000));
            setSecondsLeft(left);
            if (left <= 0) onClose(item.id);
        };
        const iv = setInterval(tick, 250);
        return () => clearInterval(iv);
    }, [stopped, item.fading, item.duration, item.id, item.nonce, onClose]);

    const running = !stopped;                       // footer stays through the fade so the card exits as one piece
    const shownSeconds = Math.max(1, secondsLeft);
    const stop = () => setStopped(true);
    const runAction = () => { try { item.action?.onClick?.(); } finally { onClose(item.id); } };

    return (
        <div
            style={{
                transition: `opacity ${fadeMs}ms ease, transform ${fadeMs}ms ease`,
                animation: item.fading ? undefined : 'toast-in 240ms cubic-bezier(0.16,1,0.3,1)',
            }}
            className={`pointer-events-auto relative w-full overflow-hidden rounded-[8px] border border-[#E4E4E1] bg-white text-[#1A1A1A] shadow-[0_6px_18px_-8px_rgba(0,0,0,0.18),0_1px_2px_rgba(0,0,0,0.04)] ${item.fading ? 'translate-y-[6px] opacity-0' : 'translate-y-0 opacity-100'}`}
        >
            {/* Live region = header + body only. The ticking footer sits OUTSIDE it,
                otherwise screen readers would re-announce the whole toast every second. */}
            <div role={item.type === 'error' || item.type === 'warning' ? 'alert' : 'status'}>
            <div className="flex items-start gap-[8px] py-[9px] pl-[10px] pr-[4px]">
                <span className="flex h-[20px] shrink-0 items-center">
                    <ToastIcon kind={tone.icon} color={tone.color} />
                </span>
                <div className="flex min-w-0 flex-1 flex-col items-start gap-[2px]">
                    <p className="w-full break-words text-[14px] font-medium leading-[20px]">
                        <span className="sr-only">{tone.label}: </span>{item.title}
                    </p>
                    {/* collapsible body */}
                    {hasBody && !collapsed && item.description && (
                        <p className="w-full break-words text-[12.5px] leading-[18px] text-[#6B6B6B]">{item.description}</p>
                    )}
                    {hasBody && !collapsed && item.action && (
                        <button
                            type="button"
                            onClick={runAction}
                            className="btn btn-secondary mb-[3px] mt-[6px]"
                        >
                            {item.action.label}
                        </button>
                    )}
                </div>
                <div className="-my-[1px] flex shrink-0 items-center gap-[2px]">
                    {hasBody && (
                        <button
                            type="button"
                            onClick={() => setCollapsed(c => !c)}
                            aria-expanded={!collapsed}
                            aria-label={`${collapsed ? 'Show' : 'Hide'} details for ${item.title}`}
                            className={ICON_BTN}
                        >
                            <Chevron up={!collapsed} />
                        </button>
                    )}
                    <button type="button" onClick={() => onClose(item.id)} aria-label={`Close ${tone.label.toLowerCase()} notification: ${item.title}`} className={ICON_BTN}>
                        <Cross />
                    </button>
                </div>
            </div>
            </div>

            {/* countdown footer, click anywhere on it to keep the toast. Static
                accessible name; the visible sentence is decorative for AT. */}
            {running && item.countdown && (
                <button
                    type="button"
                    onClick={stop}
                    aria-label={`Stop auto-close of: ${item.title}`}
                    className="relative block w-full border-t border-[#F0F0EE] bg-[#F8F8F7] px-[11px] pt-[6px] pb-[8px] text-left text-[12px] leading-[1.4] text-[#8A8A87] transition-colors hover:bg-[#F3F3F1]"
                >
                    <span aria-hidden="true">
                        This message will close in <span className="font-semibold text-[#1A1A1A]">{shownSeconds}</span> {shownSeconds === 1 ? 'second' : 'seconds'}.{' '}
                        <span className="font-semibold text-[#1A1A1A]">Click to stop.</span>
                    </span>
                    <span
                        key={item.nonce || 0}
                        aria-hidden="true"
                        className="absolute bottom-0 left-0 h-[2px] rounded-r-full"
                        style={{
                            background: tone.color,
                            width: '100%',
                            animation: `toast-progress ${item.duration}ms linear forwards`,
                        }}
                    />
                </button>
            )}
        </div>
    );
}

function NotificationStack({ items, onClose, fadeMs }) {
    return (
        <div className="pointer-events-none fixed bottom-5 right-5 z-[100] flex w-[360px] max-w-[calc(100vw-40px)] flex-col items-stretch justify-end gap-[6px]">
            {items.map(n => <Toast key={n.id} item={n} onClose={onClose} fadeMs={fadeMs} />)}
        </div>
    );
}
